import {
  createFileRoute,
  Link,
  Outlet,
  useLocation
} from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { staticFunctionMiddleware } from '@tanstack/start-static-server-functions';
import matter from 'gray-matter';
import Cookies from 'js-cookie';
import { marked } from 'marked';
import { useEffect, useState } from 'react';
import CookieConsent from 'react-cookie-consent';
import newsMarkdown from '../assets/news.md?raw';
import Footer from '../components/footer/footer';
import Header from '../components/header/header';
import NewsBanner from '../components/news-banner/news-banner';
import type { Theme } from '../types/theme';
import { themeAttribute, themeStorageKey } from '../types/theme';
import { storage } from '../utils/local-storage-service';
import {
  consentCookieName,
  initTracking,
  startTrackingWithConsent
} from '../utils/tracking';

const fetchBannerData = createServerFn({ method: 'GET' })
  .middleware([staticFunctionMiddleware])
  .handler(async () => {
    const { data: frontmatter, content } = matter(newsMarkdown);
    const html = await marked.parse(content);

    return {
      content: html,
      id: frontmatter.id,
      expiry: frontmatter.expiry
    };
  });

export const Route = createFileRoute('/_layout')({
  loader: async () => {
    const newsBanner = await fetchBannerData();
    return { newsBanner };
  },
  component: LayoutComponent
});

function LayoutComponent() {
  const { newsBanner } = Route.useLoaderData();
  const { pathname } = useLocation();
  const page = pathname.split('/')[1];
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  // `null` until the saved theme is read (it's already applied to the page by the theme init script, see __root.tsx)
  const [theme, setTheme] = useState<Theme | null>(null);

  useEffect(() => {
    setShowCookieConsent(!Cookies.get(consentCookieName));
    initTracking();
    // Scroll animations of the elements with a data-aos attribute (new ones are detected automatically).
    // Imported here: the package accesses `window` when it's imported, so it can't be imported during prerendering.
    import('@reimujs/aos')
      .then(({ default: AOS }) => AOS.init())
      .catch(error =>
        console.error('Failed to load the scroll animations', error)
      );
  }, []);

  useEffect(() => {
    const savedTheme = storage.get<Theme>(themeStorageKey);
    setTheme(savedTheme ?? 'light');
  }, []);

  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute(themeAttribute, theme);
      storage.set(themeStorageKey, theme);
    }
  }, [theme]);

  const handleThemeChange = (value: Theme) => {
    setTheme(value);
  };

  return (
    <div
      className={`page ${page && page + '-page'} ${showCookieConsent ? 'cookie-consent-visible' : ''}`}
    >
      <div className="top">
        <NewsBanner
          content={newsBanner.content}
          id={newsBanner.id}
          expiry={newsBanner.expiry}
        />
        <Header theme={theme ?? 'light'} onThemeChange={handleThemeChange} />
      </div>
      <main>
        <Outlet />
      </main>
      <Footer />
      {showCookieConsent && (
        <CookieConsent
          disableStyles={true}
          cookieName={consentCookieName}
          containerClasses="cookie-consent"
          contentClasses="cookie-consent-content"
          buttonText="OK 👍"
          buttonClasses="accept-button button button-primary"
          enableDeclineButton
          declineButtonText="Nem, köszi"
          declineButtonClasses="decline-button button button-secondary"
          sameSite="lax"
          ariaAcceptLabel="Sütik elfogadása"
          ariaDeclineLabel="Sütik elutasítása"
          onDecline={() => {
            setShowCookieConsent(false);
          }}
          onAccept={() => {
            startTrackingWithConsent();
            setShowCookieConsent(false);
          }}
        >
          A felhasználói élmény fokozása érdekében{' '}
          <Link to="/legal/cookie-policy">sütiket</Link> használunk.
        </CookieConsent>
      )}
    </div>
  );
}
